import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "@/components/Motion";
import ReelPlayer from "@/components/ReelPlayer";
import { SERVICES, CAPS } from "@/lib/site";
import usePageTitle from "@/lib/usePageTitle";

export default function Services() {
  usePageTitle("Services — 3D Printing, Laser Engraving, Embroidery, DTF & More | Skies The Limit Creations LLC");
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) {
      const t = setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
      return () => clearTimeout(t);
    }
  }, [hash]);

  return (
    <div className="grain">
      <header className="relative overflow-hidden pb-8 pt-[130px] lg:pt-[160px]">
        <div aria-hidden className="pointer-events-none absolute -top-32 right-[-10%] h-[420px] w-[420px] rounded-full bg-ember/15 blur-[130px]" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8" data-testid="services-header">
          <Reveal>
            <p className="eyebrow mb-5">What we make</p>
            <h1 className="max-w-3xl font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Plain talk about every service.
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-mist sm:text-lg">
              No jargon, no fine print. Here's what each service is, what it's good for, and how to get started.
              Anything we haven't confirmed yet is marked as a placeholder.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <nav aria-label="Jump to a service" className="mt-10 flex gap-3 overflow-x-auto pb-2">
              {SERVICES.map((s) => (
                <a
                  key={s.slug}
                  href={`#${s.slug}`}
                  data-testid={`services-jump-${s.slug}`}
                  className="chip whitespace-nowrap hover:border-gold/50 hover:text-white"
                >
                  {s.name}
                </a>
              ))}
              <a href="#cap-wall" data-testid="services-jump-cap-wall" className="chip whitespace-nowrap hover:border-gold/50 hover:text-white">
                The Cap Wall
              </a>
            </nav>
          </Reveal>
        </div>
      </header>

      <main>
        {SERVICES.map((s, i) => (
          <section
            key={s.slug}
            id={s.slug}
            className="scroll-mt-24 border-t border-white/10 py-16 lg:py-24"
            data-testid={`services-section-${s.slug}`}
          >
            <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 sm:px-8 lg:grid-cols-2 lg:gap-16">
              <Reveal className={i % 2 === 1 ? "lg:order-2" : ""}>
                {s.videoFirst && s.video ? (
                  <div className="space-y-5">
                    <div className={`grid gap-5 ${s.video2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
                      <ReelPlayer
                        src={s.video}
                        srcWebm={s.videoWebm}
                        poster={s.videoPoster}
                        label={s.videoAlt}
                        testId={`services-reel-${s.slug}`}
                        className={s.video2 ? "aspect-[9/16]" : "aspect-[3/4]"}
                        fit="contain"
                      />
                      {s.video2 && (
                        <ReelPlayer
                          src={s.video2.src}
                          srcWebm={s.video2.webm}
                          poster={s.video2.poster}
                          label={s.video2.alt}
                          testId={`services-reel-${s.slug}-2`}
                          className="aspect-[9/16]"
                          fit="contain"
                        />
                      )}
                    </div>
                    {(s.videoCaption || s.video2) && (
                      <p className="-mt-3 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-mist">
                        {s.videoCaption || "Real work — in motion"}
                      </p>
                    )}
                    <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
                      {(s.sectionImages || [s.image, s.extraImage].filter(Boolean).map((src) => ({ src, alt: s.imageAlt || s.name }))).map((im) => (
                        <figure key={im.src} className="overflow-hidden rounded-2xl border border-white/10">
                          <img src={im.src} alt={im.alt || s.name} loading="lazy" className="aspect-square w-full object-cover" />
                        </figure>
                      ))}
                    </div>
                  </div>
                ) : s.image && !(s.videoReplacesImage && s.video) ? (
                  <div className="space-y-5">
                    <figure className="overflow-hidden rounded-2xl border border-white/10">
                      <img src={s.image} alt={s.imageAlt || s.name} loading="lazy" className="aspect-[4/3] w-full object-cover" />
                      <figcaption className="bg-panel px-4 py-3 text-xs text-mist">Real work from our shop</figcaption>
                    </figure>
                    {s.extraImage && (
                      <figure className="overflow-hidden rounded-2xl border border-white/10">
                        <img src={s.extraImage} alt={s.extraImageAlt || s.name} loading="lazy" className="aspect-[4/3] w-full object-cover" />
                        <figcaption className="bg-panel px-4 py-3 text-xs text-mist">{s.extraImageCaption || "Real work from our shop"}</figcaption>
                      </figure>
                    )}
                    {s.extraImage2 && (
                      <figure className="overflow-hidden rounded-2xl border border-white/10">
                        <img src={s.extraImage2} alt={s.extraImage2Alt || s.name} loading="lazy" className="aspect-[4/3] w-full object-cover" />
                        <figcaption className="bg-panel px-4 py-3 text-xs text-mist">{s.extraImage2Caption || "Real work from our shop"}</figcaption>
                      </figure>
                    )}
                  </div>
                ) : s.videoReplacesImage && s.video ? (
                  <figure className="overflow-hidden rounded-2xl border border-white/10">
                    <ReelPlayer
                      src={s.video}
                      srcWebm={s.videoWebm}
                      poster={s.videoPoster}
                      label={s.videoAlt}
                      testId={`services-reel-${s.slug}`}
                      className="aspect-video"
                    />
                    <figcaption className="bg-panel px-4 py-3 text-xs text-mist">
                      Real work — machine embroidery in motion
                    </figcaption>
                  </figure>
                ) : !(s.sectionImages && s.sectionImages.length) ? (
                  <div className="placeholder-frame aspect-[4/3]">
                    <s.icon className="h-10 w-10 text-gold/70" aria-hidden />
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist">[ {s.name} photo coming soon ]</p>
                    <p className="text-xs text-mist/60">Real work only — we never use stock photos.</p>
                  </div>
                ) : null}
                {s.sectionImages && !s.videoFirst && (
                  <div className={`mt-5 grid grid-cols-2 gap-5 ${s.sectionImages.length > 4 ? "sm:grid-cols-3" : ""}`}>
                    {s.sectionImages.map((im) => (
                      <figure key={im.src} className="overflow-hidden rounded-2xl border border-white/10">
                        <img src={im.src} alt={im.alt || s.name} loading="lazy" className="aspect-square w-full object-cover" />
                      </figure>
                    ))}
                  </div>
                )}
                {s.video && !s.videoReplacesImage && !s.videoFirst && (
                  <figure className="mt-5 overflow-hidden rounded-2xl border border-white/10">
                    <ReelPlayer
                      src={s.video}
                      srcWebm={s.videoWebm}
                      poster={s.videoPoster}
                      label={s.videoAlt}
                      testId={`services-video-${s.slug}`}
                      className="aspect-video"
                    />
                    <figcaption className="bg-panel px-4 py-3 text-xs text-mist">
                      From our workshop reel — real pieces, engraved in-house
                    </figcaption>
                  </figure>
                )}
              </Reveal>

              <Reveal delay={0.1}>
                <p className="eyebrow mb-4">{s.tag}</p>
                <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">{s.name}</h2>
                <p className="mt-5 leading-relaxed text-mist">{s.what}</p>
                <ul className="mt-7 space-y-3">
                  {s.examples.map((ex) => (
                    <li key={ex} className="flex gap-3 text-sm text-white/85">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-ember" aria-hidden />
                      {ex}
                    </li>
                  ))}
                </ul>
                {s.detailNote && (
                  <p className="mt-7 rounded-xl border border-dashed border-gold/40 bg-gold/5 px-4 py-3 text-xs leading-relaxed text-gold/90" data-testid={`services-detail-note-${s.slug}`}>
                    {s.detailNote}
                  </p>
                )}
                <div className="mt-9 flex flex-wrap items-center gap-4">
                  <Link
                    to={`/quote?s=${s.slug}`}
                    data-testid={`services-quote-button-${s.slug}`}
                    className="btn-primary !px-6 !py-3 text-xs"
                  >
                    Quote {s.name} <ArrowRight className="h-4 w-4" aria-hidden />
                  </Link>
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-mist">
                    Good for: {s.goodFor.join(" · ")}
                  </span>
                </div>
              </Reveal>
            </div>
          </section>
        ))}

        {/* The Cap Wall — raised 3D embroidery caps */}
        <section id="cap-wall" className="scroll-mt-24 border-t border-white/10 py-16 lg:py-24" data-testid="cap-wall-section">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <Reveal>
              <p className="eyebrow mb-4">The cap wall</p>
              <div className="flex flex-wrap items-end justify-between gap-6">
                <h2 className="max-w-xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                  Defined by detail — the cap collection.
                </h2>
                <p className="max-w-sm text-sm leading-relaxed text-mist">
                  Raised 3D embroidery on premium structured caps, in the colors you rep. More designs are on the way —
                  send yours and it joins the wall.
                </p>
              </div>
            </Reveal>
            <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {CAPS.map((c, i) => (
                <Reveal key={c.src} delay={(i % 3) * 0.08}>
                  <figure className="group overflow-hidden rounded-2xl border border-white/10">
                    <img
                      src={c.src}
                      alt={c.alt}
                      loading="lazy"
                      className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <figcaption className="bg-panel px-4 py-3">
                      <p className="font-display text-sm font-bold text-white">{c.title}</p>
                      <p className="text-xs text-mist">{c.sub}</p>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
              <div className="placeholder-frame min-h-[280px]" data-testid="cap-wall-next-slot">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">[ Next cap design ]</p>
                <p className="max-w-[220px] text-xs leading-relaxed text-mist/70">
                  Reserved — more designs dropping soon
                </p>
              </div>
            </div>
            <Reveal>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/quote?s=embroidery" data-testid="cap-wall-quote-button" className="btn-primary !px-6 !py-3 text-xs">
                  Design your cap <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-mist">
                  Raised 3D embroidery · premium structured twill · classic flat brim
                </span>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
    </div>
  );
}
